import { withMotion } from '@hoc/withMotion';
import { useScrollDirection } from '@hooks/useScrollDirection';
import Button from '@stories/Components/Buttons/Button/Button';
import SkipLinkStyles from '@stories/Components/Misc/SkipLink/SkipLink.styles';
import * as React from 'react';
import S from './ScrollArrow.styles';
const ScrollArrow = (props) => {
    const [scrollVisible, setScrollVisible] = React.useState(false);
    const [overrideToTop, setOverrideToTop] = React.useState(false);
    const [currentStep, setCurrentStep] = React.useState(0); // Track the current skip section
    const scrollDirection = useScrollDirection();
    const scrollVariants = {
        hidden: {
            opacity: 0,
            y: 20,
        },
        visible: {
            opacity: 1,
            y: 0,
        },
    };
    // Track scroll position and update currentStep
    React.useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const windowHeight = window.innerHeight;
            const docHeight = document.documentElement.scrollHeight;
            setScrollVisible(scrollY > 250);
            // Override to "Scroll to Top" if near the bottom of the page
            setOverrideToTop(scrollY + windowHeight >= docHeight - 400);
            // Update currentStep based on scroll position
            const skipButtons = Array.from(document.querySelectorAll(SkipLinkStyles.Button));
            let step = 0;
            for (let i = 0; i < skipButtons.length; i++) {
                const button = skipButtons[i];
                const rect = button.getBoundingClientRect();
                const sectionTop = rect.top + window.scrollY;
                if (sectionTop <= scrollY + windowHeight / 2) {
                    step = i;
                }
            }
            setCurrentStep(step);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    return (React.createElement(S.Container, { variants: scrollVariants, initial: "hidden", animate: scrollVisible ? 'visible' : 'hidden', "data-hidden": !scrollVisible, "data-scroll-direction": overrideToTop ? 'up' : scrollDirection },
        React.createElement(Button, { buttonType: "primary", iconOnly: true, "data-scroll": true, onClick: handleScrollDirectionClick }, getAccessibleText())));
    /**
     * Handles the click event for the scroll arrow button.
     */
    function handleScrollDirectionClick() {
        if (overrideToTop || scrollDirection === 'up') {
            scrollToTop();
        }
        else {
            scrollToNextSection(currentStep, setCurrentStep);
        }
    }
    /**
     * Scrolls to the top of the page.
     */
    function scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCurrentStep(0); // Reset to first section
    }
    /**
     * Gets the accessible text for the scroll arrow button.
     * @returns The accessible text for the scroll arrow button.
     */
    function getAccessibleText() {
        if (overrideToTop) {
            return props.translations?.['global.scrollTop'] ?? '';
        }
        switch (scrollDirection) {
            case 'up':
                return props.translations?.['global.scrollTop'] ?? '';
            case 'down':
                return props.translations?.['global.scrollToNextSection'] ?? '';
            default:
                return '';
        }
    }
};
/**
 * Scrolls to the next section on the page.
 * This is done by finding the next skip link button and scrolling to its section.
 */
export function scrollToNextSection(currentStep, setCurrentStep) {
    const skipButtons = Array.from(document.querySelectorAll(SkipLinkStyles.Button));
    // Prevent scrolling past the last section
    if (currentStep >= skipButtons.length - 1) {
        scrollToBottom();
        return;
    }
    const nextButton = skipButtons[currentStep + 1]; // Get the next section button
    if (nextButton) {
        const rect = nextButton.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        window.scrollTo({ top: sectionTop - 49, behavior: 'smooth' });
        setCurrentStep?.(currentStep + 1); // Update step after scroll
    }
}
/**
 * Scrolls to the bottom of the page.
 */
function scrollToBottom() {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
}
export default withMotion(ScrollArrow);
